import { Box } from "../../../components/ui/box";
import { Button } from "../../../components/ui/button";
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "../../../components/ui/empty";
import { Icon, type IconName } from "../../../components/ui/icon";
import { ScrollArea } from "../../../components/ui/scroll-area";
import { Text } from "../../../components/ui/text";
import type { Omittable } from "../../../lib/props";

// Same compaction as WidgetEmpty's `sm`: the error state always sits inside a
// widget card, never a page section. DESIGN-TUNABLE.
const EMPTY_SM_CLASS = "gap-3 px-4 py-6 md:py-6";

// The package compiles with `"types": []` (no @types/node); bundlers replace
// `process.env.NODE_ENV` statically, so a minimal ambient shape is all the
// dev/prod branch below needs.
declare const process: { env: { NODE_ENV?: string } };

/** Serializes an error to readable JSON. `name`/`message`/`stack` don't survive
 *  `JSON.stringify` on an `Error` (it emits `{}`), so pull them explicitly, then
 *  fold in any own-enumerable extras a richer error carries (e.g. a data layer's
 *  `graphQLErrors`/`networkError`) — generically, so the kit stays domain-free and
 *  never names a specific client. */
function toDetail(error: Error): string {
  const extras: Record<string, unknown> = {};
  const bag = error as unknown as Record<string, unknown>;
  for (const key of Object.keys(error)) {
    extras[key] = bag[key];
  }
  return JSON.stringify(
    { name: error.name, message: error.message, ...extras, stack: error.stack },
    (_key, value) => (value === undefined ? undefined : value),
    2,
  );
}

/**
 * THE error state every widget body uses — the counterpart to {@link WidgetEmpty}.
 * Shown when a widget's data query fails (the widget returns it on `error`) OR when
 * its render throws (the harness's error boundary falls back to it). In local dev
 * (`NODE_ENV !== 'production'`) it also renders the serialized error JSON in a scrollable mono block
 * so the failure is visible at a glance; in production it degrades to the title.
 */
export function WidgetError({
  error,
  title = "Couldn't load widget",
  icon = "alert-triangle",
  onRetry,
}: Omittable<{
  error?: Error | null;
  title?: string;
  icon?: IconName;
  /** When set (harness error-boundary fallback), renders a "Try again" button that
   *  resets the boundary so a transient render error isn't stuck until remount. */
  onRetry?: () => void;
}>) {
  const detail =
    process.env.NODE_ENV !== 'production' && error ? toDetail(error) : null;
  return (
    <Box fill align="center" justify="center">
      <Empty className={EMPTY_SM_CLASS}>
        <EmptyHeader>
          {/* `circle` for the same reason as WidgetEmpty: this state lives
              inside a card. The destructive glyph overrides the well's muted
              tint. */}
          <EmptyMedia variant="circle">
            <Icon name={icon} size="md" color="destructive" />
          </EmptyMedia>
          <EmptyTitle className="text-base">{title}</EmptyTitle>
          {detail ? (
            <EmptyDescription>This widget hit an error while rendering.</EmptyDescription>
          ) : null}
        </EmptyHeader>
        {onRetry || detail ? (
          <EmptyContent>
            {onRetry ? (
              <Button size="sm" variant="outline" onClick={onRetry}>
                Try again
              </Button>
            ) : null}
            {detail ? (
              <Box
                surface="sunken"
                direction="column"
                rounded="md"
                maxHeight={140}
                fill="width"
                overflow="hidden"
              >
                <ScrollArea className="max-h-[140px]">
                  <Box padding="xs">
                    <Text mono size="xs" variant="destructive" align="left" className="whitespace-pre-wrap break-all">
                      {detail}
                    </Text>
                  </Box>
                </ScrollArea>
              </Box>
            ) : null}
          </EmptyContent>
        ) : null}
      </Empty>
    </Box>
  );
}
